import { GroupRole, GroupRoles } from "api/group";
import { AugmentedGroup } from "store/appDataSlice";

/**
 * Check if a user has a specific role in a group.
 * Does NOT include the admin bypass — callers handle that separately.
 *
 * @param group
 * @param userId
 * @param role
 */
export function hasGroupRole(group: AugmentedGroup | undefined | null, userId: number, role: GroupRole): boolean {
  if (!group) return false;
  return group.roles[role]?.includes(userId) ?? false;
}

/**
 * Immutably add a user to a role's array.
 *
 * @param roles
 * @param userId
 * @param role
 */
export function addGroupRole(roles: GroupRoles, userId: number, role: GroupRole): GroupRoles {
  const existing = roles[role] ?? [];
  if (existing.includes(userId)) return roles;
  return { ...roles, [role]: [...existing, userId] };
}

/**
 * Immutably remove a user from a role's array.
 *
 * @param roles
 * @param userId
 * @param role
 */
export function removeGroupRole(roles: GroupRoles, userId: number, role: GroupRole): GroupRoles {
  const existing = roles[role];
  if (!existing) return roles;
  const updated = existing.filter((id) => id !== userId);
  return { ...roles, [role]: updated };
}

/**
 * Count the number of roles a user has.
 *
 * @param roles
 * @param userId
 * @returns The number of roles the user has
 */
export function getUserRolesCount(roles: GroupRoles | undefined, userId: number): number {
  if (!roles) return 0;
  return Object.values(roles).filter((roleUsers) => roleUsers?.includes(userId)).length;
}
